import { Component, Input, OnDestroy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * How much of the screen the busy state is allowed to take.
 *
 *  - `bar`      A thin indeterminate bar at the top of the nearest positioned
 *               ancestor. Nothing is covered and nothing is blocked. This is the
 *               default, and the right choice for an ordinary save or delete:
 *               the button the user just pressed already shows its own spinner
 *               and is disabled, so covering the page adds nothing.
 *  - `scoped`   Bar plus a light scrim over this container only, for when the
 *               content underneath would be misleading while the write is in
 *               flight (a list mid-reorder, a tree mid-rebuild).
 *  - `blocking` Bar, scrim and a centred card over the whole viewport. Reserve it
 *               for destructive or bulk work the user must not navigate away from.
 */
export type KubaBusyMode = 'bar' | 'scoped' | 'blocking';

/**
 * Progress feedback for a write in flight.
 *
 * Replaces the legacy `.panel-loader` overlay, which covered the entire screen for
 * every save, appeared and vanished instantly (so a fast request read as a grey
 * flash), and told assistive technology nothing.
 *
 * Two timings do most of the usability work here:
 *
 *  - Nothing renders for the first `delayMs`. A request that returns in 80ms
 *    produces no visual event at all, instead of a flicker the eye reads as a bug.
 *  - Once shown, it stays for at least `minVisibleMs`, so a request that resolves
 *    just past the delay does not strobe.
 */
@Component({
  selector: 'kuba-busy',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    @if (visible) {
      <div
        class="kuba-busy"
        [class.kuba-busy--scoped]="mode === 'scoped'"
        [class.kuba-busy--blocking]="mode === 'blocking'"
      >
        <div class="kuba-busy__bar" aria-hidden="true"><span></span></div>
        @if (mode !== 'bar') {
          <div class="kuba-busy__card">
            <span class="loader-icon"></span>
            <span class="kuba-busy__message">{{ message | translate }}</span>
          </div>
        }
      </div>
    }
    <!-- Announced once when the write starts, not on every change-detection pass. -->
    <span class="kuba-busy__sr" role="status" aria-live="polite">
      {{ visible ? (message | translate) : '' }}
    </span>
  `,
  styleUrls: ['./busy.component.scss'],
})
export class KubaBusyComponent implements OnDestroy {
  /** Translation key describing the work, e.g. `SAVE_PROCESS` or `PROCESSING`. */
  @Input() message = 'PROCESSING';

  @Input() mode: KubaBusyMode = 'bar';

  /** Requests faster than this never render anything. */
  @Input() delayMs = 250;

  /** Once visible, stay at least this long so the indicator cannot strobe. */
  @Input() minVisibleMs = 400;

  visible = false;

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt = 0;
  private busyValue = false;

  @Input()
  set busy(value: boolean) {
    const next = value === true;
    if (next === this.busyValue) {
      return;
    }
    this.busyValue = next;
    if (next) {
      this.scheduleShow();
    } else {
      this.scheduleHide();
    }
  }
  get busy(): boolean {
    return this.busyValue;
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private scheduleShow(): void {
    this.clearTimers();
    if (this.visible) {
      // Still on screen from a previous write; keep it up rather than restarting.
      this.shownAt = Date.now();
      return;
    }
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.visible = true;
      this.shownAt = Date.now();
    }, Math.max(0, this.delayMs));
  }

  private scheduleHide(): void {
    this.clearTimers();
    if (!this.visible) {
      return;
    }
    const remaining = this.minVisibleMs - (Date.now() - this.shownAt);
    if (remaining <= 0) {
      this.visible = false;
      return;
    }
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.visible = false;
    }, remaining);
  }

  private clearTimers(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
